/**
 * Servicing activity form (business pages slice 3).
 *
 * Creates and edits organization servicing activity: Subject (required), Activity Type, Activity Date
 * (required), Call Plan and Results (separate fields, saved independently), Next Task Due and Related
 * Goal / Session. The picklists come from admin-editable tables, never from constants.
 *
 * Also edits existing rows written elsewhere without changing what they mean:
 *  - legacy `description` text is shown and edited as neutral "Notes", never as Call Plan/Results;
 *  - event-reward rows (migration 2026-10-05-220): organization follows the event, read-only here;
 *  - support-task rows: organization follows the customer and may be empty.
 * Participants and attachments were removed: their service functions wrote columns that do not
 * exist (interaction_participants / interaction_attachments), so every attempt failed.
 */

import React, { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createInteraction,
  updateInteraction,
} from "../../services/interactions.service";
import {
  buildInteractionUpdatePayload,
  buildServicingCreatePayload,
  getLookupValues,
  isRewardInteraction,
  isSupportTaskInteraction,
  validateServicingForm,
  type ServicingFormValues,
} from "../../services/interactionServicing.service";
import { getOrganizations } from "../../services/organizations.service";
import { getLocations } from "../../services/locations.service";
import { sanitizePostgrestSearchTerm } from "../../utils/postgrestSearch";
import {
  INTERACTION_PURPOSES,
  INTERACTION_METHODS,
  QUERY_KEYS,
} from "../../utils/constants";
import type {
  Interaction,
  InteractionLookupValue,
} from "../../types/interaction.types";
import type { UUID } from "../../types/common.types";
import { useAuth } from "../../contexts/AuthContext";
import Button from "../common/Button";
import Input from "../common/Input";
import Select from "../common/Select";
import Textarea from "../common/Textarea";
import { preventEnterSubmit } from "../../utils/preventEnterSubmit";

interface InteractionFormProps {
  interaction?: Interaction;
  organizationId?: UUID;
  locationId?: UUID;
  onSuccess?: () => void;
  onCancel?: () => void;
}

const localToday = (): string => {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

export const LOOKUP_QUERY_KEY = "interaction-lookups";

/** Active values, plus the row's current value if it has since been retired (so it still shows). */
const optionsFor = (
  values: InteractionLookupValue[] | undefined,
  currentId?: string | null,
) => (values || []).filter((v) => v.is_active || v.id === currentId);

const InteractionForm: React.FC<InteractionFormProps> = ({
  interaction,
  organizationId: propOrgId,
  locationId: propLocationId,
  onSuccess,
  onCancel,
}) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isEditing = !!interaction;
  const isReward = isRewardInteraction(interaction);
  const isSupportTask = isSupportTaskInteraction(interaction);
  // The organization is fixed by the page (organization detail) or, for reward rows, by the event.
  const fixedOrganizationId = propOrgId;

  const [formData, setFormData] = useState<ServicingFormValues>({
    title: interaction?.title || "",
    subject_id: interaction?.subject_id || "",
    activity_type_id: interaction?.activity_type_id || "",
    goal_session_id: interaction?.goal_session_id || "",
    interaction_date: interaction?.interaction_date
      ? interaction.interaction_date.split("T")[0]
      : localToday(),
    interaction_time: interaction?.interaction_time
      ? String(interaction.interaction_time).slice(0, 5)
      : "",
    next_follow_up_date: interaction?.next_follow_up_date
      ? interaction.next_follow_up_date.split("T")[0]
      : "",
    call_plan: interaction?.call_plan || "",
    results: interaction?.results || "",
    description: interaction?.description || "",
    purpose: interaction?.purpose || "check-in",
    method: interaction?.method || "phone_call",
    organization_id: interaction?.organization_id || propOrgId || "",
    location_id: interaction?.location_id || propLocationId || "",
    duration_minutes: interaction?.duration_minutes?.toString() || "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [orgSearch, setOrgSearch] = useState("");

  const lookups = {
    subjects: useQuery({
      queryKey: [LOOKUP_QUERY_KEY, "subjects"],
      queryFn: () => getLookupValues("subjects"),
    }),
    activityTypes: useQuery({
      queryKey: [LOOKUP_QUERY_KEY, "activity_types"],
      queryFn: () => getLookupValues("activity_types"),
    }),
    goalSessions: useQuery({
      queryKey: [LOOKUP_QUERY_KEY, "goal_sessions"],
      queryFn: () => getLookupValues("goal_sessions"),
    }),
  };
  const subjectOptions = optionsFor(
    lookups.subjects.data,
    interaction?.subject_id,
  );
  const activityTypeOptions = optionsFor(
    lookups.activityTypes.data,
    interaction?.activity_type_id,
  );
  const goalOptions = optionsFor(
    lookups.goalSessions.data,
    interaction?.goal_session_id,
  );
  const subjectsArePlaceholders = subjectOptions.some((s) => s.is_placeholder);

  // Organization picker: only on the global Interactions page for an ordinary row.
  const showOrgPicker = !fixedOrganizationId && !isReward;
  const orgTerm = sanitizePostgrestSearchTerm(orgSearch);
  const { data: orgResults } = useQuery({
    queryKey: [QUERY_KEYS.ORGANIZATIONS, "interaction-form", orgTerm],
    queryFn: () =>
      getOrganizations({
        page: 1,
        limit: 25,
        search: orgTerm || undefined,
        sortBy: "name",
        sortOrder: "asc",
      }),
    enabled: showOrgPicker,
  });
  const orgOptions = useMemo(() => {
    const list = (orgResults?.data || []).map((o) => ({
      id: o.id,
      name: o.name,
    }));
    if (
      formData.organization_id &&
      !list.some((o) => o.id === formData.organization_id)
    ) {
      list.unshift({
        id: formData.organization_id,
        name: "(current organization)",
      });
    }
    return list;
  }, [orgResults, formData.organization_id]);

  const effectiveOrgId = fixedOrganizationId || formData.organization_id;
  const { data: locationsData } = useQuery({
    queryKey: [QUERY_KEYS.LOCATIONS, "interaction-form", effectiveOrgId],
    queryFn: () => getLocations(effectiveOrgId, { page: 1, limit: 100 }),
    enabled: !!effectiveOrgId && !isReward,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.INTERACTIONS] });
  };
  const createMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      createInteraction(
        payload as unknown as Omit<
          Interaction,
          "id" | "created_at" | "updated_at"
        >,
      ),
    onSuccess: () => {
      invalidate();
      onSuccess?.();
    },
  });
  const updateMutation = useMutation({
    mutationFn: ({
      id,
      updates,
    }: {
      id: UUID;
      updates: Record<string, unknown>;
    }) => updateInteraction(id, updates as Partial<Interaction>),
    onSuccess: () => {
      invalidate();
      onSuccess?.();
    },
  });

  const subjectLabel = subjectOptions.find(
    (s) => s.id === formData.subject_id,
  )?.label;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    const newErrors = validateServicingForm(formData, {
      isEditing,
      original: interaction,
      fixedOrganizationId,
    });
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0 || !user) {
      return;
    }
    try {
      if (isEditing && interaction) {
        const updates = buildInteractionUpdatePayload(interaction, formData, {
          fixedOrganizationId,
          subjectLabel,
        });
        if (Object.keys(updates).length === 0) {
          onSuccess?.();
          return;
        }
        await updateMutation.mutateAsync({ id: interaction.id, updates });
      } else {
        await createMutation.mutateAsync(
          buildServicingCreatePayload(formData, {
            userId: user.id,
            organizationId: fixedOrganizationId,
            subjectLabel,
          }),
        );
      }
    } catch (error) {
      const message =
        error && typeof error === "object" && "message" in error
          ? String((error as { message: unknown }).message)
          : "The activity could not be saved.";
      setSubmitError(message);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;
  const showNotes = isEditing && !!interaction?.description;
  const showGoal = goalOptions.length > 0;

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-5"
      noValidate
      data-testid="servicing-activity-form"
    >
      {isReward && (
        <div className="rounded-md bg-blue-50 border border-blue-200 p-3 text-sm text-blue-800">
          Event reward record. Its organization follows the event and cannot be
          changed here; the original issuer is kept.
        </div>
      )}
      {isSupportTask && !interaction?.organization_id && (
        <div className="rounded-md bg-gray-50 border border-gray-200 p-3 text-sm text-gray-700">
          Logged from a support task for a customer with no organization.
        </div>
      )}

      {/* Subject + Activity Type */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Select
            label="Subject / Task Type"
            name="subject_id"
            value={formData.subject_id}
            onChange={handleChange}
            error={errors.subject_id}
            required={!isEditing}
            fullWidth
            data-testid="servicing-subject"
          >
            <option value="">Select a subject</option>
            {subjectOptions.map((s) => (
              <option key={s.id} value={s.id}>
                {s.label}
                {s.is_active ? "" : " (retired)"}
              </option>
            ))}
          </Select>
          {subjectsArePlaceholders && (
            <p className="mt-1 text-xs text-amber-700">
              Placeholder list: the business's subject list has not been
              supplied yet. An admin can replace it under Manage lists.
            </p>
          )}
        </div>
        <Select
          label="Activity Type"
          name="activity_type_id"
          value={formData.activity_type_id}
          onChange={handleChange}
          fullWidth
          data-testid="servicing-activity-type"
        >
          <option value="">None</option>
          {activityTypeOptions.map((a) => (
            <option key={a.id} value={a.id}>
              {a.label}
              {a.is_active ? "" : " (retired)"}
            </option>
          ))}
        </Select>
      </div>

      {/* Dates */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Input
          label="Activity Date"
          name="interaction_date"
          type="date"
          value={formData.interaction_date}
          onChange={handleChange}
          error={errors.interaction_date}
          required
          fullWidth
          data-testid="servicing-date"
        />
        <Input
          label="Next Task Due"
          name="next_follow_up_date"
          type="date"
          value={formData.next_follow_up_date}
          onChange={handleChange}
          error={errors.next_follow_up_date}
          fullWidth
          data-testid="servicing-next-due"
        />
        <Input
          label="Time"
          name="interaction_time"
          type="time"
          value={formData.interaction_time}
          onChange={handleChange}
          fullWidth
        />
      </div>

      {/* Call Plan + Results, side by side */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Textarea
          label="Call Plan"
          name="call_plan"
          value={formData.call_plan}
          onChange={handleChange}
          rows={5}
          fullWidth
          placeholder="What you intend to cover"
          data-testid="servicing-call-plan"
        />
        <Textarea
          label="Results"
          name="results"
          value={formData.results}
          onChange={handleChange}
          rows={5}
          fullWidth
          placeholder="Outcome after the interaction"
          data-testid="servicing-results"
        />
      </div>
      <p className="-mt-3 text-xs text-gray-500">
        Call Plan and Results are internal: only CRM staff can see them.
      </p>

      {showNotes && (
        <Textarea
          label="Notes"
          name="description"
          value={formData.description}
          onChange={handleChange}
          rows={3}
          fullWidth
          helperText="Recorded before Call Plan / Results existed, or by another screen."
          data-testid="servicing-notes"
        />
      )}

      {showGoal && (
        <Select
          label="Related Goal / Session"
          name="goal_session_id"
          value={formData.goal_session_id}
          onChange={handleChange}
          fullWidth
        >
          <option value="">None</option>
          {goalOptions.map((g) => (
            <option key={g.id} value={g.id}>
              {g.label}
              {g.is_active ? "" : " (retired)"}
            </option>
          ))}
        </Select>
      )}

      <Input
        label="Title (optional)"
        name="title"
        value={formData.title}
        onChange={handleChange}
        fullWidth
        placeholder="Defaults to the subject"
      />

      {/* Organization / Location */}
      {showOrgPicker && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Organization
              {!isSupportTask && <span className="text-red-500"> *</span>}
            </label>
            <input
              type="text"
              value={orgSearch}
              onKeyDown={preventEnterSubmit}
              onChange={(e) => setOrgSearch(e.target.value)}
              placeholder="Search organizations..."
              className="block w-full mb-2 px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            <select
              name="organization_id"
              value={formData.organization_id}
              onChange={handleChange}
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              data-testid="servicing-organization"
            >
              <option value="">
                {isSupportTask ? "No organization" : "Select organization"}
              </option>
              {orgOptions.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
            {errors.organization_id && (
              <p className="mt-1 text-sm text-red-600">
                {errors.organization_id}
              </p>
            )}
          </div>
          <Select
            label="Location"
            name="location_id"
            value={formData.location_id}
            onChange={handleChange}
            disabled={!effectiveOrgId || !!propLocationId}
            fullWidth
          >
            <option value="">No specific location</option>
            {(locationsData?.data || []).map((loc) => (
              <option key={loc.id} value={loc.id}>
                {loc.name}
              </option>
            ))}
          </Select>
        </div>
      )}
      {!showOrgPicker &&
        !isReward &&
        (locationsData?.data || []).length > 0 && (
          <Select
            label="Location"
            name="location_id"
            value={formData.location_id}
            onChange={handleChange}
            disabled={!!propLocationId}
            fullWidth
          >
            <option value="">No specific location</option>
            {(locationsData?.data || []).map((loc) => (
              <option key={loc.id} value={loc.id}>
                {loc.name}
              </option>
            ))}
          </Select>
        )}

      {/* Purpose / Method: required by the database (enums) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Select
          label="Purpose (Why)"
          name="purpose"
          value={formData.purpose}
          onChange={handleChange}
          fullWidth
        >
          {INTERACTION_PURPOSES.map((p) => (
            <option key={p.value} value={p.value}>
              {p.label}
            </option>
          ))}
        </Select>
        <Select
          label="Method (How)"
          name="method"
          value={formData.method}
          onChange={handleChange}
          fullWidth
        >
          {INTERACTION_METHODS.map((m) => (
            <option key={m.value} value={m.value}>
              {m.label}
            </option>
          ))}
        </Select>
        <Input
          label="Duration (minutes)"
          name="duration_minutes"
          type="number"
          value={formData.duration_minutes}
          onChange={handleChange}
          error={errors.duration_minutes}
          fullWidth
        />
      </div>

      {(submitError || Object.keys(errors).length > 0) && (
        <div
          role="alert"
          className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-800"
          data-testid="servicing-form-error"
        >
          {submitError || "Please fix the highlighted fields."}
        </div>
      )}

      <div className="flex justify-end space-x-3 pt-4 border-t">
        {onCancel && (
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button
          type="submit"
          variant="primary"
          isLoading={isLoading}
          data-testid="servicing-save"
        >
          {isEditing ? "Save Activity" : "Add Activity"}
        </Button>
      </div>
    </form>
  );
};

export default InteractionForm;
